import mixpanel from "mixpanel-browser";
import { useAuthStore } from "@/store/useAuthStore";
import { isSupportSession } from "@/utils/supportSession";
import { v4 as uuidv4 } from "uuid";
import { debugWarn } from "@/utils/debugLog";

const STORAGE_KEY_SESSION_ID = "mixpanel_session_id";

// Env-gated, default off: session recorder froze notepad screenshots on Chromebooks (ENGG-2906).
const MIXPANEL_ENABLED = import.meta.env.VITE_MIXPANEL_ENABLED === "true";

const pendingQueue: any[] = [];
let mixpanelInitialized = false;
const activeTimers = new Set<string>();

enum mixpanelEvents {
  STANDARDS_SUMMARY_OPENED = "standards_summary_opened",
  STANDARDS_BREADCRUMB_CHANGED = "standards_breadcrumb_changed",
  STANDARDS_VIEW_TOGGLED = "standards_view_toggled",
  STANDARDS_DOMAIN_OPENED = "standards_domain_opened",
  STANDARDS_DOMAIN_GRADE_OPENED = "standards_domain_grade_opened",
  STANDARDS_HOVER_OPENED = "standards_hover_opened",
  SESSION_START = "session_start",
  SESSION_END = "session_end",

  LESSON_START = "lesson_started",
  LESSON_END = "lesson_end",

  SLIDES_STARTED = "slides_started",
  SLIDES_ENDED = "slides_ended",

  CLASSROOM = "classroom",

  SLIDE_START = "slides_started",
  SLIDE_END = "slides_end",

  STUDENT_INTERACTION = "student_interaction",

  AI_MESSAGE = "ai_message",
  STUDENT_MESSAGE = "student_message",

  SPACE_BAR = "space_bar",

  RESUME_MODAL_SHOWN = "resume_modal_shown",
  RESUME_SELECTED = "resume_selected",
  RESTART_BITE_SELECTED = "restart_bite_selected",
  RESTART_LESSON_SELECTED = "restart_lesson_selected",
}

enum StudentInteractionTypes {
  PREVIOUS_SLIDE = "previous_slide",
  NEXT_SLIDE = "next_slide",
  END_LESSON = "end_lesson",
  BLOB_CLICKED = "blob_clicked",
  SPACE_BAR = "space_bar",
}

enum SpaceBarInteractionTypes {
  FOLLOW = "follow",
  INTERRUPTS = "interrupts",
}

interface SessionStartProperties {}

interface SessionEndProperties {
  reason: string;
}

interface LessonStartProperties {
  lesson_id: string;
  name: string;
  assigned_to?: string[];
  assigned: boolean;
}

interface LessonEndProperties {
  lesson_id?: string;
  name?: string;
  completion_percentage?: number;
  total_slides?: number;
  slides_completed?: number;
  assigned_to?: string[];
  assigned?: boolean;
  reason?: string;
}

interface SlideStartProperties {
  slide_id: string;
  lesson_name: string;
}

interface SlideEndProperties {
  slide_id: string;
  lesson_name: string;
}

interface StudentInteractionProperties {
  type: StudentInteractionTypes;
  lesson_id?: string;
}

interface SpaceBarProperties {
  type: SpaceBarInteractionTypes;
}

interface ResumeFlowProperties {
  session_id?: string;
  lesson_id?: string;
  slide_index?: number;
  entry_source?: "refresh" | "dashboard" | "reconnect" | "lti";
  state_version?: number;
}

const _processPendingQueue = () => {
  try {
    while (pendingQueue.length > 0) {
      const pendingRequest = pendingQueue.shift();
      if (pendingRequest.type === "event") {
        _sendMixpanelEvent(pendingRequest.eventName, pendingRequest.properties);
      } else if (pendingRequest.type === "start_timer") {
        _startMixpanelTimer(pendingRequest.eventName);
      } else if (pendingRequest.type === "stop_timer") {
        _stopMixpanelTimer(
          pendingRequest.eventName,
          pendingRequest.properties,
          pendingRequest.config,
        );
      } else if (pendingRequest.type === "identify") {
        _mixpanelIdentify(pendingRequest.userId, pendingRequest.userProperties);
      }
    }
  } catch (error) {
    console.error("Error processing Mixpanel queue:", error);
  }
};

const getSessionId = () => {
  let sessionId = sessionStorage.getItem(STORAGE_KEY_SESSION_ID);

  if (!sessionId) {
    sessionId = uuidv4();
    sessionStorage.setItem(STORAGE_KEY_SESSION_ID, sessionId);
  }

  return sessionId;
};

const resetSessionId = () => {
  const newSessionId = uuidv4();
  sessionStorage.setItem(STORAGE_KEY_SESSION_ID, newSessionId);
  return newSessionId;
};

const _mixpanelIdentify = (
  userId: any,
  userProperties: Record<string, any>,
) => {
  try {
    // A support session is authenticated as the student, so every event here
    // would be attributed to that child. Suppressed rather than tagged: these
    // feed product analytics and experiment arms, where an operator's clicks
    // are noise regardless of how they are labelled.
    if (isSupportSession()) return;
    mixpanel.identify(userId);
    mixpanel.people.set(userProperties);
  } catch (error) {
    console.error("Error identifying Mixpanel user:", error);
  }
};

const _sendMixpanelEvent = (
  eventName: mixpanelEvents,
  properties: Record<string, any> = {},
) => {
  try {
    // A support session is authenticated as the student, so every event here
    // would be attributed to that child. Suppressed rather than tagged: these
    // feed product analytics and experiment arms, where an operator's clicks
    // are noise regardless of how they are labelled.
    if (isSupportSession()) return;
    const { user } = useAuthStore.getState();
    const sessionId = getSessionId();
    if (user) {
      mixpanel.track(eventName, {
        ...properties,
        user_id: user.id,
        role: user.role,
        session_id: sessionId,
        time: new Date().toISOString(),
      });
    }
  } catch (error) {
    console.error("Error sending Mixpanel event:", error);
  }
};

const _startMixpanelTimer = (eventName: string) => {
  try {
    mixpanel.time_event(eventName);
    activeTimers.add(eventName);
  } catch (error) {
    console.error("Error starting Mixpanel timer:", error);
  }
};

const _stopMixpanelTimer = (
  eventName: mixpanelEvents,
  properties: Record<string, any> = {},
  config: Record<string, any> = {},
) => {
  try {
    // A support session is authenticated as the student, so every event here
    // would be attributed to that child. Suppressed rather than tagged: these
    // feed product analytics and experiment arms, where an operator's clicks
    // are noise regardless of how they are labelled.
    if (isSupportSession()) return;
    if (!activeTimers.has(eventName)) {
      return;
    }
    const { user } = useAuthStore.getState();
    const sessionId = getSessionId();
    mixpanel.track(
      eventName,
      {
        ...properties,
        user_id: user?.id,
        role: user?.role,
        session_id: sessionId,
        time: new Date().toISOString(),
      },
      config,
    );
    activeTimers.delete(eventName);
  } catch (error) {
    console.error("Error stopping Mixpanel timer:", error);
  }
};

const initMixpanel = () => {
  if (!MIXPANEL_ENABLED) return;
  try {
    const token = import.meta.env.VITE_MIXPANEL_TOKEN;
    if (!token) {
      debugWarn("Mixpanel token not found in environment variables");
      return;
    }

    mixpanel.init(token, {
      api_host: "https://analytics.evertutor.ai",
      debug: true,
      persistence: "localStorage",
      record_sessions_percent: 100,
      loaded: () => {
        mixpanelInitialized = true;
        _processPendingQueue();
      },
    });

    getSessionId();
  } catch (error) {
    console.error("Error initializing Mixpanel:", error);
  }
};

const identifyMixpanelUser = (
  userId: any,
  userProperties: Record<string, any>,
) => {
  if (!MIXPANEL_ENABLED) return;
  try {
    if (!mixpanelInitialized) {
      pendingQueue.push({
        type: "identify",
        userId,
        userProperties,
      });
      return;
    }
    _mixpanelIdentify(userId, userProperties);
  } catch (error) {
    console.error("Error in identifyMixpanelUser:", error);
  }
};

const mixpanelEvent = (
  eventName: mixpanelEvents,
  properties: Record<string, any> = {},
) => {
  if (!MIXPANEL_ENABLED) return;
  try {
    if (!mixpanelInitialized) {
      pendingQueue.push({
        type: "event",
        eventName: eventName,
        properties: properties,
      });
      return;
    }
    _sendMixpanelEvent(eventName, properties);
  } catch (error) {
    console.error("Error in mixpanelEvent:", error);
  }
};

const startMixpanelTimer = (eventName: mixpanelEvents) => {
  if (!MIXPANEL_ENABLED) return;
  try {
    if (!mixpanelInitialized) {
      pendingQueue.push({
        type: "start_timer",
        eventName,
      });
      return;
    }
    _startMixpanelTimer(eventName);
  } catch (error) {
    console.error("Error in startMixpanelTimer:", error);
  }
};

const stopMixpanelTimer = (
  eventName: mixpanelEvents,
  properties: Record<string, any> = {},
  config: Record<string, any> = {},
) => {
  try {
    if (!activeTimers.has(eventName)) {
      return;
    }
    if (!mixpanelInitialized) {
      pendingQueue.push({
        type: "stop_timer",
        eventName,
        properties,
        config,
      });
      return;
    }
    _stopMixpanelTimer(eventName, properties, config);
  } catch (error) {
    console.error("Error in stopMixpanelTimer:", error);
  }
};

const hasActiveTimer = (eventName: mixpanelEvents): boolean => {
  return activeTimers.has(eventName);
};

const resetMixpanel = () => {
  if (!MIXPANEL_ENABLED) return;
  try {
    activeTimers.clear();
    pendingQueue.length = 0;
    mixpanel.reset();
  } catch (error) {
    console.error("Error resetting Mixpanel:", error);
  }
};

const trackSessionStart = (properties: SessionStartProperties) => {
  startMixpanelTimer(mixpanelEvents.SESSION_END);
  mixpanelEvent(mixpanelEvents.SESSION_START, properties);
};

const trackSessionEnd = (
  properties: SessionEndProperties,
  config: Record<string, any> = {},
) => {
  stopMixpanelTimer(mixpanelEvents.SESSION_END, properties, config);
};

const trackLessonStart = (properties: LessonStartProperties) => {
  startMixpanelTimer(mixpanelEvents.LESSON_END);
  mixpanelEvent(mixpanelEvents.LESSON_START, properties);
};

const trackLessonEnd = (properties: LessonEndProperties) => {
  stopMixpanelTimer(mixpanelEvents.LESSON_END, properties);
};

const trackSlideStart = (properties: SlideStartProperties) => {
  startMixpanelTimer(mixpanelEvents.SLIDE_END);
  mixpanelEvent(mixpanelEvents.SLIDE_START, properties);
};

const trackSlideEnd = (properties: SlideEndProperties) => {
  stopMixpanelTimer(mixpanelEvents.SLIDE_END, properties);
};

const trackStudentInteraction = (properties: StudentInteractionProperties) => {
  mixpanelEvent(mixpanelEvents.STUDENT_INTERACTION, properties);
};

const trackSpaceBarInteraction = (properties: SpaceBarProperties) => {
  mixpanelEvent(mixpanelEvents.SPACE_BAR, properties);
};

const trackStudentMessage = (properties: {
  lesson_id: string;
  type: string;
}) => {
  mixpanelEvent(mixpanelEvents.STUDENT_MESSAGE, properties);
};

const trackAiMessage = () => {
  mixpanelEvent(mixpanelEvents.AI_MESSAGE);
};

const trackResumeModalShown = (properties: ResumeFlowProperties) => {
  mixpanelEvent(mixpanelEvents.RESUME_MODAL_SHOWN, properties);
};

const trackResumeSelected = (properties: ResumeFlowProperties) => {
  mixpanelEvent(mixpanelEvents.RESUME_SELECTED, properties);
};

const trackRestartBiteSelected = (properties: ResumeFlowProperties) => {
  mixpanelEvent(mixpanelEvents.RESTART_BITE_SELECTED, properties);
};

const trackRestartLessonSelected = (properties: ResumeFlowProperties) => {
  mixpanelEvent(mixpanelEvents.RESTART_LESSON_SELECTED, properties);
};

export {
  mixpanelEvents,
  mixpanelEvent,
  StudentInteractionTypes,
  SpaceBarInteractionTypes,
  initMixpanel,
  identifyMixpanelUser,
  hasActiveTimer,
  trackSessionStart,
  trackSessionEnd,
  trackLessonStart,
  trackLessonEnd,
  trackSlideStart,
  trackSlideEnd,
  trackStudentMessage,
  trackAiMessage,
  trackResumeModalShown,
  trackResumeSelected,
  trackRestartBiteSelected,
  trackRestartLessonSelected,
  trackStudentInteraction,
  trackSpaceBarInteraction,
  resetMixpanel,
  resetSessionId,
};
